import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        secondary: 'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive: 'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        outline: 'text-foreground',
        // QOps domain variants (preserved from original)
        primary: 'border-transparent bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
        success: 'border-transparent bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        warning: 'border-transparent bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
        danger: 'border-transparent bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
        info: 'border-transparent bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
        purple: 'border-transparent bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
        orange: 'border-transparent bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <span className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

// Domain helper: maps status strings to badge variants
interface StatusBadgeProps {
  status: string;
  className?: string;
}

function StatusBadge({ status, className }: StatusBadgeProps) {
  const variantMap: Record<string, BadgeProps['variant']> = {
    NUEVO: 'primary',
    EN_ANALISIS: 'purple',
    EN_DESARROLLO: 'info',
    EN_PRUEBAS: 'info',
    RESUELTO: 'success',
    CERRADO: 'secondary',
    CANCELADO: 'danger',
    PENDIENTE: 'warning',
    EN_PROCESO: 'primary',
    EN_ESPERA: 'orange',
    EN_REVISION: 'purple',
    COMPLETADA: 'success',
    NUEVA: 'primary',
    ESPERA_SOPORTE: 'orange',
    ESPERA_CLIENTE: 'warning',
    EN_PAUSA: 'secondary',
  };

  const variant = variantMap[status] || 'secondary';
  const label = status.replace(/_/g, ' ');

  return (
    <Badge variant={variant} className={className}>
      {label}
    </Badge>
  );
}

// Domain helper: maps priority strings to badge variants
interface PriorityBadgeProps {
  priority: string;
  className?: string;
}

function PriorityBadge({ priority, className }: PriorityBadgeProps) {
  const variantMap: Record<string, BadgeProps['variant']> = {
    CRITICA: 'danger',
    URGENTE: 'danger',
    ALTA: 'orange',
    MEDIA: 'warning',
    BAJA: 'success',
    AAA: 'danger',
    AA: 'orange',
    A: 'warning',
    B: 'primary',
    C: 'secondary',
  };

  const variant = variantMap[priority] || 'secondary';

  return (
    <Badge variant={variant} className={className}>
      {priority}
    </Badge>
  );
}

export { Badge, badgeVariants, StatusBadge, PriorityBadge };
